import { sendRequest } from "@/core/hooks/useApiQueries/sendRequest";

export type CheckoutCycle = "monthly" | "annual";

export type PlanCatalogFeatures = {
  hasFollowUps: boolean;
  hasIntegrations: boolean;
  hasBulkSend: boolean;
  hasCustomIntegrations: boolean;
};

export type PlanCatalogItem = {
  slug: string;
  name: string;
  price_brl_cents: number | null;
  price_annual_brl_cents: number | null;
  leads_per_month_limit: number | null;
  whatsapp_connections_limit: number | null;
  features: PlanCatalogFeatures;
};

/**
 * SA-1279 — cupom aplicado à conta, como a tela de planos precisa dele.
 *
 * `percent_off_*` é o percentual **real** lido do Stripe, com duas casas: no
 * anual ele é `pct/12` (3,33% para "40% do primeiro mês"), porque o Stripe
 * aplica cupom por FATURA. Todo valor em reais exibido sai daqui, nunca do
 * número da claim, senão a tela promete R$ 133,00 e a fatura cobra R$ 132,87.
 *
 * `null` num ciclo = sem desconto ali (cupom só-mensal, ou percentual que não
 * deu para ler): a tela mostra a claim e não promete valor.
 */
export type PromoOffer = {
  code: string;
  headline: string;
  perk_note: string | null;
  perk_plan_slugs: string[];
  percent_off_monthly: number | null;
  percent_off_annual: number | null;
};

/** Catálogo + o cupom da conta. O `promo` é irmão da lista, não campo de plano. */
export type PlanCatalog = {
  plans: PlanCatalogItem[];
  promo: PromoOffer | null;
};

/** Integração do catálogo instalada (`active`) em algum setor da empresa. */
export type TrialIntegrationInUse = { slug: string; name: string };

export type TrialRecap = {
  leadsCaptured: number;
  conversationsHandled: number;
  /**
   * SA-1360 P4-C: integrações em uso na empresa inteira, uma por integração.
   * Só o admin da empresa recebe a lista; os demais, `[]`. Opcional para o
   * recap antigo (sem o campo = nenhuma).
   */
  integrationsInUse?: TrialIntegrationInUse[];
};

/** Settings globais não-sensíveis de billing (kill switch + ciclo + trial). */
export type BillingSettings = {
  enforcement_enabled: boolean;
  default_billing_cycle: CheckoutCycle;
  default_trial_days: number;
};

export async function fetchBillingSettings(
  token: string | undefined
): Promise<BillingSettings> {
  const response = await sendRequest(token, {
    method: "GET",
    url: "/billing/settings",
  });
  return response.data.data;
}

/**
 * Oferta dos códigos da URL de cadastro (SA-1218, SA-1279). Rota **pública**: é
 * consultada na página de signup, antes de existir usuário. Devolve só se o link
 * dá período grátis e a claim do cupom — o modo do link fica no servidor, e o
 * percentual real só sai em `/billing/plans`, que é autenticado.
 */
export type SalesEntryOffer = {
  has_trial: boolean;
  promo: { code: string; headline: string } | null;
};

export async function fetchSalesEntryOffer(
  code: string | null,
  promoCode: string | null
): Promise<SalesEntryOffer> {
  const params = new URLSearchParams();
  if (code) params.set("code", code);
  if (promoCode) params.set("promo", promoCode);

  const response = await sendRequest(undefined, {
    method: "GET",
    url: `/billing/sales-entry-offer?${params.toString()}`,
  });
  return response.data.data;
}

/**
 * Catálogo de planos visíveis + cupom da conta (onboarding + paywall).
 *
 * `sectorId` escopa a conta de onde sai o cupom: o backend resolve a Company pelo
 * acesso daquele setor e, sem ele, cai no primeiro acesso ativo do usuário.
 */
export async function fetchPlans(
  token: string | undefined,
  sectorId?: string | null
): Promise<PlanCatalog> {
  const response = await sendRequest(token, {
    method: "GET",
    url: sectorId
      ? `/billing/plans?sectorId=${encodeURIComponent(sectorId)}`
      : "/billing/plans",
  });
  return {
    plans: response.data.data?.plans ?? [],
    promo: response.data.data?.promo ?? null,
  };
}

/**
 * Atualiza a subscription trialing para o plano/ciclo escolhidos e abre o
 * Stripe Checkout em modo setup. Retorna a URL hospedada para redirecionar.
 */
export async function createCheckoutSession(
  token: string | undefined,
  sectorId: string,
  planSlug: string,
  cycle: CheckoutCycle
): Promise<{ url: string; first_charge_cents: number | null }> {
  const response = await sendRequest(token, {
    method: "POST",
    url: `/billing/checkout-session/${sectorId}`,
    data: { data: { plan_slug: planSlug, cycle } },
  });
  return response.data.data;
}

/**
 * Confirma de forma síncrona o cartão salvo ao retornar do Stripe Checkout
 * (passa o session_id do success_url). Reconcilia `has_payment_method` sem
 * depender do webhook. Idempotente.
 */
export async function confirmCheckout(
  token: string | undefined,
  sectorId: string,
  sessionId: string
): Promise<{ has_payment_method: boolean }> {
  const response = await sendRequest(token, {
    method: "POST",
    url: `/billing/checkout-confirm/${sectorId}`,
    data: { data: { session_id: sessionId } },
  });
  return response.data.data;
}

/** Recap do trial (leads + conversas reais da Company) p/ o paywall. */
export async function fetchTrialRecap(
  token: string | undefined,
  sectorId: string
): Promise<TrialRecap> {
  const response = await sendRequest(token, {
    method: "GET",
    url: `/billing/trial-recap/${sectorId}`,
  });
  return response.data.data;
}

// --- Gestão de pagamento (SA-1120, Configurações → Faturamento) ---

export type PortalFlow = "payment_method_update" | "subscription_update";

export type BillingSummary = {
  subscription_status: string | null;
  next_charge_at: string | null;
  trial_ends_at: string | null;
  has_payment_method: boolean;
  cycle: CheckoutCycle;
  active_contacts_used: number | null;
  active_contacts_limit: number | null;
  usage_period_ends_at: string | null;
  // Contas legadas/internas (active sem subscription no Stripe) não conseguem
  // upgrade self-service via Portal → o CTA de uso roteia para o comercial.
  can_self_upgrade: boolean;
  /**
   * Cupom vigente (SA-1279). `ends_at` é o espelho do desconto no Stripe: nulo
   * quando não há desconto ativo (a fatura já consumiu o cupom `once` e o webhook
   * zerou o campo). `percent_off` é o percentual real do ciclo vigente, e é o que
   * permite dizer QUANTO vai ser cobrado; `null` quando não deu para ler.
   */
  promo: {
    code: string;
    headline: string;
    ends_at: string | null;
    percent_off: number | null;
  } | null;
  plan: {
    name: string | null;
    slug: string | null;
    price_brl_cents: number | null;
    price_annual_brl_cents: number | null;
    leads_per_month_limit: number | null;
  } | null;
};

export type CardSummary = {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
} | null;

/** Resumo do plano/assinatura da Company (Admins-only). */
export async function fetchBillingSummary(
  token: string | undefined,
  companyId: string
): Promise<BillingSummary> {
  const response = await sendRequest(token, {
    method: "GET",
    url: `/billing/summary/${companyId}`,
  });
  return response.data.data;
}

/** Cartão default lido on-demand do Stripe (Admins-only). `null` se não houver. */
export async function fetchPaymentMethod(
  token: string | undefined,
  companyId: string
): Promise<CardSummary> {
  const response = await sendRequest(token, {
    method: "GET",
    url: `/billing/payment-method/${companyId}`,
  });
  return response.data.data ?? null;
}

/** Cria a sessão do Stripe Customer Portal. `flow` escolhe cartão (default) ou troca de plano. */
export async function createPortalSession(
  token: string | undefined,
  companyId: string,
  flow: PortalFlow = "payment_method_update"
): Promise<{ url: string }> {
  const response = await sendRequest(token, {
    method: "POST",
    url: `/billing/portal-session/${companyId}`,
    data: { flow },
  });
  return response.data.data;
}
